import React, { useContext, useState, Fragment, useEffect, useLayoutEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import PatientDetailHeader from "../PatientDetailHeader/PatientDetailHeader";
import PatientEpisodes from "../PatientEpisodes/PatientEpisodes";
import {
  Box,
  Select,
  MenuItem,
  Checkbox,
  CircularProgress,
  Switch,
  Typography,
  Theme,
  SelectChangeEvent,
  Button,
  useTheme,
} from "@mui/material";
import GetPatient from "./GetPatient";
import { useQuery, useLazyQuery } from "@apollo/client/react";
import { GetPatient as GetPatientQueryType } from "../PatientDetailsPage/types/GetPatient";
import { GetPatient_patient } from "./types/GetPatient";
import AppContext from "../../context/AppContext";
import PatientReportsFull from "../PatientReportsFull/PatientReportsFull";
import Loading from "../Loading/Loading";
import { EpisodeReportsProps } from "../ReportViewer/EpisodeReportsProps";
import { CumulativeContext } from "../../context/CumulativeContext";
import CumulativePanel from "../CumulativePanel/CumulativePanel";
import TestSearch from "../TestSearch/TestSearch";
import { PatientDetailsContext } from "../../context/PatientDetailsContext";
import authService from "../../services/authService";
import { Scrollbars }from "react-custom-scrollbars-2";
import ErrorPopover from "../common/ErrorPopover/ErrorPopover";
import { PrintMenuIcon, DownloadMenuIcon, ShareMenuIcon } from "../PatientReportsFull/PrintMenu/PrintComponents";
import sortby from "./sort_by.json";
import HtmlTooltip from "../common/HtmlTooltip/HtmlTooltip";
import {
  GetPrintableReports,
  GetPrintableReportsVariables,
  GetPrintableReports_episodereports_reports as dReport,
  GetPrintableReports_patient as dPatient,
} from "../PrintableReports/types/GetPrintableReports";
import getPrintableReports from "../PrintableReports/GetPrintableReportQuery";
import axios from "axios";
import get from "lodash/get";
import * as Constants from "../../constants";
import { setVerticalScrollbar } from "../../utils/setVerticalScrollbar";
import SuccessPopover from "../common/SuccessPopover/SuccessPopover";
import PrintFrame from "../ReportViewer/PrintFrame";
import { isTenantVet } from "../../services/tenantService";
import thm from "../theme";
import EorderDialog from "../EorderDialog/EorderDialog";
import eOrderLogoMobile from "../../assets/eOrderButtonMobile.svg";
import eOrderLogoVetMobile from "../../assets/eOrderButtonVetMobile.svg";
import { eResultsOptions, getSiteOption, showEorderButton } from "../../utils/siteOptions";
import { validateProvider } from "../../utils/provider";
import { downloadHelper } from "../../utils/downloadHelper";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import DialogBoxShare from "../DialogBox/DialogBoxShare";
import PopUpCard from "./OdaPopUpCard";
import { validateLabNumber, validateLabNumberDigitOnly } from "../../utils/helpers";

dayjs.extend(timezone);

type PathParams = {
  id: string;
  allPatients: string;
  labnoDigitOnly?: string;
  labnumber: string;
  sortBy: string;
};

const createStyles = (theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
  },
  menuItem: {
    backgroundColor: thm.palette.primary.light,
    borderBottomColor: "white",
  },
  selectInput: {
    borderRadius: "4px",
    border: "solid 1px #979797",
    backgroundColor: "#f7f7f7",
    height: "40px",
    marginTop: "8px",
    width: "100%",
  },
  selectInputAuto: {
    borderRadius: "4px",
    border: "solid 1px #979797",
    backgroundColor: "#f7f7f7",
    height: "40px",
    marginTop: "8px",
    width: "auto",
  },
  selectInputMobile: {
    borderRadius: "4px",
    border: "solid 1px #979797",
    backgroundColor: "#f7f7f7",
    height: "40px",
    marginTop: "20px",
    width: "98%",
    maxWidth: "450px",
  },
  hr: {
    marginTop: "15px",
    width: "98%",
    border: "solid 1px " + thm.palette.primary.light,
  },
  btnPrint: {
    borderRadius: "25px",
    height: "35px",
  },
  eOrderLogoMobile: {
    width: "45px",
    margin: "-5px 0 -4px 0",
  },
  eOrderButtonSmall: {
    backgroundColor: "#ffffff !important",
    borderRadius: "20px !important",
    marginTop: "3px",
    fontSize: "1em !important",
    textTransform: "none",
    border: "solid 2px #753bbd !important",
    height: "32px !important",
    "&:hover": {
      backgroundColor: "#ede1f7 !important",
      color: "white !important",
    },
  },
});

const useWindowSize = () => {
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    function updateHeight() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", updateHeight);
    updateHeight();
    return () => window.removeEventListener("resize", updateHeight);
  }, []);
  return height;
};

const PatientDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { initialLabNo, setInitialLabNo, labnoDigitOnly, setLabnoDigitOnly } = useContext(PatientDetailsContext);
  const pathParams = useParams<PathParams>();
  const id: number = parseInt(pathParams.id || "0");
  const isDesktop = useContext(AppContext).isDesktop;
  const { setError } = useContext(AppContext);
  const { isCumulative } = useContext(CumulativeContext);
  const allPatients =
    pathParams.allPatients === "1" &&
    authService.getAllowAllPatients() &&
    authService.getAllowAllPatientsAcknownledged();

  // Get query parameters and validate
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const labnoDigitOnlyParam = searchParams.get("labnoDigitOnly");
    const labnumberParam = searchParams.get("labnumber");

    if (!labnoDigitOnlyParam) {
      navigate("/not-found", { replace: true });
      return;
    }

    setLabnoDigitOnly(labnoDigitOnlyParam ? labnoDigitOnlyParam : "UNKNOWN");

    if (labnoDigitOnlyParam && !validateLabNumberDigitOnly(labnoDigitOnlyParam)) {
      navigate("/not-found", { replace: true });
      return;
    }

    if (labnumberParam && !validateLabNumber(labnumberParam)) {
      navigate("/not-found", { replace: true });
      return;
    }
    else {
      setInitialLabNo(labnumberParam || "");
    }
  }, [location.search, navigate]);

  const [urno, setUrno] = useState("");
  const [ward, setWard] = useState("");
  const [referringDoctor, setReferringDoctor] = useState("");
  const [isODAPopupVisible, setIsODAPopupVisible] = useState(false);
  const [previousReports, setPreviousReports] = useState<any[]>([]);
  const mobility = authService.isMobility();

  const getCookie = (cname: string) => {
    const name = cname + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return "";
  };

  const [getSortBy] = useState<any>(JSON.parse(JSON.stringify(sortby)));
  const defSortEpisode = getCookie("sortEpisode") === "" ? "dateCollected" : getCookie("sortEpisode");
  const [sortBy, setSortBy] = useState(defSortEpisode);
  const [sortData, setSortData] = useState("");
  const [reportIdSelected, setReportIdSelected] = useState<any[]>([]);
  const [allReportIds, setAllReportIds] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [rescroll, setRescroll] = useState(true);
  const [panel, setPanel] = useState(-1);
  const [print, setPrint] = useState(false);
  const height = useWindowSize();
  const [openEorderDialog, setOpenEorderDialog] = React.useState(false);
  const [_ready, setReady] = React.useState(false);
  const [showGenEorderButton, setShowGenEorderButton] = React.useState(false);
  const [openShare, setOpenShare] = useState(false);
  const eOrderFeature = (getSiteOption(eResultsOptions.EORDER_FEATURE) as string) === "ON";
  const siteOptions = getSiteOption(eResultsOptions.EORDER_BLACKLIST) as string[];
  const odaOptions = (getSiteOption(eResultsOptions.SHARE_TO_FEATURE) as any)?.value?.oda;
  const [allReportFetch, setAllReportFetch] = useState(false);
  const [_dreports, setDreports] = useState<dReport[]>([]);
  const [showNextComponent, setShowNextComponent] = useState(false);

  const [patientInfo, setPatientInfo] = useState<GetPatient_patient | undefined>();
  const [patientEpisodesData, setPatientEpisodesData] = useState([]);

  const handlePatientEpisodesData = (data: any) => {
    const fetchedProviderNumbers = data.episodes
      .map((panels: any) => panels.panels.map((panel: any) => panel.reportingProviderNo))
      .flat();
    const uniqueProviderNumbers = [...new Set(fetchedProviderNumbers)] as string[];
    uniqueProviderNumbers.map((value) => {
      if (showEorderButton(siteOptions, value) && validateProvider(value as string)) {
        setShowGenEorderButton(true);
      }
    });
    setPatientEpisodesData(data);
  };

  const selectSort = (event: SelectChangeEvent<string>) => {
    setSortBy(event.target.value as string);
    if (event.target.value !== "") {
      setSortData(event.target.value as string);
      setLabnoDigitOnly("UNKNOWN");
      setInitialLabNo("");
    } else {
      setSortData("");
    }
    document.cookie = "sortEpisode=" + (event.target.value as string) + ";expires=Fri, 31 Dec 9999 23:59:59 GMT";
  };

  const handleVisiblePopUp = () => {
    setIsODAPopupVisible(false);
  };

  const handleOpenNextComponent = () => {
    setShowNextComponent(true);
    setOpenShare(true);
    setIsODAPopupVisible(false);
  };

  const [showRemovedPanels, setShowRemovedPanels] = useState(authService.getShowDeletedPanels());
  const theme = useTheme();
  const classes = createStyles(theme);

  /* if (labnoDigitOnly === "UNKNOWN") {
    setLabnoDigitOnly(pathParams.labnoDigitOnly? pathParams.labnoDigitOnly : "UNKNOWN");
  }*/

  useEffect(() => {
    // Anything in here is fired on component mount.
    window.addEventListener("load", () => {
      setVerticalScrollbar(document.documentElement.clientHeight);
    });

    window.addEventListener("resize", () => {
      setVerticalScrollbar(document.documentElement.clientHeight);
    });

    return () => {
      // Anything in here is fired on component unmount.
      document.body.style.overflowY = "auto";
    };
  }, []);

  useEffect(() => {
    if (pathParams.sortBy !== undefined && pathParams.sortBy !== "" && pathParams.sortBy !== "undefined") {
      setSortBy(pathParams.sortBy);
      setSortData(pathParams.sortBy);
    } else {
      if (defSortEpisode !== "") {
        setSortData(defSortEpisode);
      } else {
        setSortData("dateCollected"); // Default ones here..
      }
    }
  }, [pathParams.sortBy, defSortEpisode]);

  const queryVariables = (): GetPrintableReportsVariables => ({
    patientId: id,
    providers: authService.getProviders(),
    includePdf: true,
    allPatients: allPatients,
    ids: reportIdSelected.map((r) => parseInt(r)),
  });

  const [getPrintableReportsQuery, { data, error: printError }] = useLazyQuery<GetPrintableReports>(
    getPrintableReports
  );

  const {
    data: patientData,
    loading: patientLoading,
    error: patientError,
  } = useQuery<GetPatientQueryType>(GetPatient, {
    variables: { patientId: id },
  });
  const patient: GetPatient_patient | undefined = patientData ? patientData.patient : undefined;
  useEffect(() => {
    if (isDownloadLoading) {
      const hasValidData = (testData: GetPrintableReports | undefined) =>
        testData && testData.episodereports && testData.episodereports.reports;

      if (hasValidData(data)) {
        const url = `${import.meta.env.VITE_ACL_REPORT_URL}/report`;
        const dreports = get(data, "episodereports.reports") || ([] as dReport[]);
        const dpatient: dPatient = get(data, "patient") || ({} as dPatient);

        const downRepData: any = {};
        downRepData.patient = dpatient;
        downRepData.reports = dreports.filter(
          (r: { reportid: any }) => reportIdSelected.find((p) => p === (r.reportid || 0).toString()) !== undefined,
        );
        downRepData.tz = dayjs.tz.guess();

        axios
          .post<any>(
            url,
            {
              output: "pdf",
              data: downRepData,
              template: isTenantVet() ? "gribbles-template.html" : "report-template.html",
            },
            { responseType: "blob" },
          )
          .then((response) => {
            downloadHelper(response.data, `reports-${labnoDigitOnly}.pdf`);
            setIsDownloadLoading(false);
            setReportIdSelected([]);
            setError("");
          })
          .catch((e) => {
            setIsDownloadLoading(false);
            setError(e.message ? e.message : e);
          });
      }
    }

    if (patient) {
      setPatientInfo(patient);
    }
    //eslint-disable-next-line
  }, [data, isDownloadLoading, reportIdSelected]);

  useEffect(() => {
    if (data) {
      const reports = get(data, "episodereports.reports") || ([] as dReport[]);
      setDreports(reports);
    }
  }, [data]);

  useEffect(() => {
    if (patientData?.patient) {
      setPatientInfo(patientData?.patient);
    }
  }, [patientData?.patient]);

  useEffect(() => {
    if (printError || patientError) {
      const errorMessage = Constants.NO_DB_CONNECTION;
      setError(errorMessage);
    }
  }, [printError, patientError, setError]);

  if (patientLoading) {
    return <Loading />;
  }

  if (patientError || printError) {
    return <ErrorPopover />;
  }

  if (!patient) {
    return null;
  } else {
    document.title = `${patient.fullnameTitleCase} - eResults Viewer`;

    // setPatientInfo(patient);
  }
  const setUpdatedCheckEpisode = (reportId: string) => {
    if (reportIdSelected.includes(reportId)) {
      const index = reportIdSelected.indexOf(reportId);
      if (index > -1) {
        const updatedReportIdSelected = reportIdSelected.slice();
        updatedReportIdSelected.splice(index, 1);
        setReportIdSelected(updatedReportIdSelected);
      }
    } else {
      //add here
      const updatedReportIdSelected = [...reportIdSelected, reportId];
      setReportIdSelected(updatedReportIdSelected);
    }
    //setRescroll(false);
  };

  const reportProps: EpisodeReportsProps = {
    patientId: id,
    labnoDigitOnly: labnoDigitOnly,
  };

  const receiveMessage = (event: any): void => {
    if (event.data && event.data === "eResults2.0 Print Ready") {
      window.removeEventListener("message", receiveMessage, false);
      setIsLoading(false);
      setIsDownloadLoading(false);
    }
  };

  const excludedReports = (episodesData: any): number[] => {
    if (!episodesData?.episodes) {
      return [];
    }
    const userProvider = authService.getProviders();
    const reportIdsNonUserProvider: number[] = [];

    episodesData.episodes.forEach((episode: any) => {
      episode.panels.forEach((panel: any) => {
        if (!userProvider.includes(panel.reportingProviderNo)) {
          reportIdsNonUserProvider.push(panel.reportid);
        }
      });
    });
    return reportIdsNonUserProvider;
  };

  const removeMatchingReportIds = (reportIdsWithMissingProvider: number[]) => {
    const updatedReportIdSelected = reportIdSelected.filter(
      (reportId) => !reportIdsWithMissingProvider.includes(reportId),
    );
    setPreviousReports(reportIdSelected);
    setReportIdSelected(updatedReportIdSelected);
  };

  const printReports = () => {
    setIsLoading(true);
    window.addEventListener("message", receiveMessage, false);

    // Defer until rerender, prevents jank
    if (!authService.isAuthenticated()) {
      navigate("/login", { state: { from: location } });
    } else {
      setTimeout(() => {
        //Marked all report id as viewed..
        setPrint(true);

        const reportsCsv: string = reportIdSelected.join(",");
        const iframe: any = document.querySelector("#printFrame");
        iframe.src = `/patient-print/${id}/1/labnumber?labnoDigitOnly=0&ids=${reportsCsv}&frame=printFrame`;
      }, 200);
    }
  };



  const downloadReports = () => {
    setIsDownloadLoading(true);
    getPrintableReportsQuery({ variables: queryVariables() });
  };

  const handleShowRemovedPanelsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    authService.setFlagUserHasChangedShowDeletedPanel();
    authService.setShowDeletedPanels(event.target.checked);
    setShowRemovedPanels(event.target.checked);
  };

  const handleEorderDialogClose = () => {
    setOpenEorderDialog(false);
  };

  const handleEorderDialogOpen = () => {
    setOpenEorderDialog(true);
  };

  const headerHeightDesktop = mobility ? 165 : 144;
  const testSearchHeight = 160;
  return (
    <>
      <Box display="flex" flexDirection="column" minHeight={isDesktop ? "99vh" : "150vh"} overflow={"hidden"}>
        <PatientDetailHeader
          patient={patient}
          latestUrNumber={urno}
          ward={ward}
          referringDoctor={referringDoctor}
        ></PatientDetailHeader>
        {isDesktop ? (
          <Box display="flex" height={height - headerHeightDesktop} width={"100%"} overflow={"hidden"}>
            <Box display={"inline"} minWidth={"400px"}>
              <Box mt={2} mb={1} mx={2}>
                <TestSearch patientId={id} labnoDigitOnly={labnoDigitOnly} allPatients={allPatients} />
                <div style={classes.root}>
                  <Box m={1} display="flex" justifyContent="space-between" flexWrap="wrap" alignItems="center">
                    <Box>
                      <HtmlTooltip
                        title={
                          <Typography display={"inline"} variant={"inherit"}>
                            Removed panels are extra panels that have been performed as a necessary step in obtaining a
                            patient test result.
                          </Typography>
                        }
                      >
                        <Typography display="inline" variant="body1">
                          <Switch
                            checked={showRemovedPanels}
                            onChange={handleShowRemovedPanelsChange}
                            name="showRemovedPanels"
                            color="primary"
                          />
                          Show Removed Panels
                        </Typography>
                      </HtmlTooltip>
                    </Box>
                  </Box>
                </div>
                {allReportIds.length > 0 ? (
                  <div style={classes.root}>
                    <Box m={1} display="flex" justifyContent="space-between" flexWrap="wrap" alignItems="center">
                      <Box>
                        <Checkbox
                          checked={reportIdSelected.length === allReportIds.length ? true : false}
                          onChange={() => {
                            const updatedReportIdSelected =
                              reportIdSelected.length === allReportIds.length ? [] : allReportIds.slice();

                            setReportIdSelected(updatedReportIdSelected);
                          }}
                          name="checkAllEpisode"
                          color="primary"
                          indeterminate={
                            reportIdSelected.length > 0 && reportIdSelected.length < allReportIds.length ? true : false
                          }
                          // className={styles.episodeCheckbox}
                        />{" "}
                        <Typography display="inline" variant="body1">
                          Select All
                        </Typography>
                      </Box>

                      <Box display="flex" flexDirection="row">
                        {!allPatients ? (
                          <Box textAlign="center" p={1}>
                            <ShareMenuIcon
                              onClick={() => {
                                setOpenShare(true);
                              }}
                              disabled={reportIdSelected.length === 0}
                            />
                          </Box>
                        ) : !odaOptions ? (
                          excludedReports(patientEpisodesData).length ? (
                            <Box textAlign="center" p={1}>
                              <ShareMenuIcon
                                onClick={() => {
                                  const nonProviderReports = excludedReports(patientEpisodesData);
                                  if (nonProviderReports.length > 0) {
                                    removeMatchingReportIds(nonProviderReports);
                                  }
                                  setIsODAPopupVisible(true);
                                }}
                                disabled={reportIdSelected.length === 0}
                              />
                            </Box>
                          ) : (
                            <Box textAlign="center" p={1}>
                              <ShareMenuIcon
                                onClick={() => {
                                  setOpenShare(true);
                                }}
                                disabled={reportIdSelected.length === 0}
                              />
                            </Box>
                          )
                        ) : (
                          <Box textAlign="center" p={1}>
                            <ShareMenuIcon
                              onClick={() => {
                                setOpenShare(true);
                              }}
                              disabled={reportIdSelected.length === 0}
                            />
                          </Box>
                        )}

                        {/* {isSmscEnabled ? (
                          <Box textAlign="center" p={1}>
                            {isQueuedLoading ? (
                              <CircularProgress size={16} />
                            ) : (
                              <SendMenuIcon
                                onClick={onQueueDownloads(
                                  reportIdSelected.map((r) => parseInt(r)),
                                  queueDownloads,
                                )}
                                disabled={isQueuedLoading || reportIdSelected.length === 0}
                              />
                            )}
                          </Box>
                        ) : null} */}
                        <Box textAlign="center" p={1}>
                          {isDownloadLoading ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DownloadMenuIcon
                              onClick={downloadReports}
                              title={"Download Selected"}
                              disabled={isDownloadLoading || reportIdSelected.length === 0}
                            />
                          )}
                        </Box>

                        {!mobility ? (
                          <Box textAlign="center" p={1}>
                            {isLoading ? (
                              <CircularProgress size={16} />
                            ) : (
                              <PrintMenuIcon
                                onClick={printReports}
                                title={"Print Selected"}
                                disabled={isLoading || reportIdSelected.length === 0}
                              />
                            )}
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                  </div>
                ) : (
                  ""
                )}
                <hr style={classes.hr}></hr>
                <div style={classes.root}>
                  <Box>
                    <Select
                      id="sort_by"
                      value={sortBy}
                      onChange={selectSort}
                      variant="outlined"
                      style={isDesktop ? classes.selectInput : classes.selectInputMobile}
                    >
                      {/* <MenuItem value={0}>Sort By</MenuItem> */}
                      {getSortBy["sort_by"].map((item: any) => {
                        return (
                          <MenuItem sx={classes.menuItem} key={item.id} value={item.value}>
                            {item.value === sortBy ? "Sort By : " : ""}
                            {item.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </Box>
                </div>
              </Box>

              <Box height={height - headerHeightDesktop - testSearchHeight} mx={0}>
                {/* <CustomScrollbar height={window.innerHeight - headerHeightDesktop - testSearchHeight} ml={2}> */}
                <PatientEpisodes
                  patientId={id}
                  setUrno={setUrno}
                  setWard={setWard}
                  setReferringDocotr={setReferringDoctor}
                  setLabnoDigitOnly={setLabnoDigitOnly}
                  labnoDigitOnly={labnoDigitOnly}
                  setInitialLabNo={setInitialLabNo}
                  allPatients={allPatients}
                  sortBy={sortData}
                  setChangeSelected={setReportIdSelected}
                  reportIdSelected={reportIdSelected}
                  setCheckEpisode={setUpdatedCheckEpisode}
                  allReportIds={allReportIds}
                  setAllReportIds={setAllReportIds}
                  allReportFetch={allReportFetch}
                  setAllReportFetch={setAllReportFetch}
                  showRemovedPanels={showRemovedPanels}
                  rescroll={rescroll}
                  setRescroll={setRescroll}
                  panel={panel}
                  setPanel={setPanel}
                  isDesktop={isDesktop}
                  print={print}
                  setPrint={setPrint}
                  patient={patientInfo}
                  onPatientEpisodesData={handlePatientEpisodesData}
                />
                {/* </CustomScrollbar> */}
              </Box>
            </Box>
            <Box display={"inline"} width={"100%"}>
              {isCumulative ? (
                <CumulativePanel
                  allPatients={allPatients}
                  sortBy={sortBy}
                  showRemovedPanels={showRemovedPanels}
                  height={height - headerHeightDesktop}
                  patient={patientInfo}
                />
              ) : (
                <PatientReportsFull
                  reportsProps={reportProps}
                  initialLabNo={initialLabNo}
                  setInitialLabNo={setInitialLabNo}
                  allPatients={allPatients}
                  sortBy={sortBy}
                  patient={patientInfo}
                />
              )}
            </Box>
          </Box>
        ) : (
          <Fragment>
            <Box mt={2} mb={0} ml={1} mr={1}>
              <TestSearch patientId={id} labnoDigitOnly={labnoDigitOnly} allPatients={allPatients} />
              <div style={classes.root}>
                <Box ml={1} display="flex" justifyContent="space-between" flexWrap="wrap" alignItems="center">
                  <Box>
                    <HtmlTooltip
                      title={
                        <Typography display={"inline"} variant={"inherit"}>
                          Removed panels are extra panels that have been performed as a necessary step in obtaining a
                          patient test result.
                        </Typography>
                      }
                    >
                      <Typography display="inline" variant="body2">
                        <Switch
                          checked={showRemovedPanels}
                          onChange={handleShowRemovedPanelsChange}
                          name="showRemovedPanels"
                          color="primary"
                        />
                        Show Removed Panels
                      </Typography>
                    </HtmlTooltip>
                  </Box>
                </Box>
              </div>
              {allReportIds.length > 0 ? (
                <div style={classes.root}>
                  <Box ml={1} display="flex" justifyContent="space-between" flexWrap="wrap" alignItems="center">
                    <Box>
                      <Checkbox
                        checked={reportIdSelected.length === allReportIds.length ? true : false}
                        onChange={() => {
                          // find a way to get the reporting_provuiderno here
                          //
                          const updatedReportIdSelected =
                            reportIdSelected.length === allReportIds.length ? [] : allReportIds.slice();

                          setReportIdSelected(updatedReportIdSelected);
                        }}
                        name="checkAllEpisode"
                        color="primary"
                        indeterminate={
                          reportIdSelected.length > 0 && reportIdSelected.length < allReportIds.length ? true : false
                        }
                        // className={styles.episodeCheckbox}
                      />{" "}
                      <Typography display="inline" variant="body2">
                        Select All
                      </Typography>
                    </Box>

                    <Box display="flex" flexDirection="row">
                      {eOrderFeature && showGenEorderButton && (
                        <>
                          <Button sx={classes.eOrderButtonSmall} data-testid="new-order-btn" onClick={handleEorderDialogOpen}>
                            <img
                              src={isTenantVet() ? eOrderLogoVetMobile : eOrderLogoMobile}
                              alt="eorder logo"
                              style={classes.eOrderLogoMobile}
                            />
                          </Button>
                          <EorderDialog
                            open={openEorderDialog}
                            fullscreen={false}
                            onClose={handleEorderDialogClose}
                            patient={patientInfo as GetPatient_patient}
                            setReady={setReady}
                          />
                        </>
                      )}
                      {!allPatients ? (
                        <Box textAlign="center" p={1}>
                          <ShareMenuIcon
                            onClick={() => {
                              setOpenShare(true);
                            }}
                            disabled={reportIdSelected.length === 0}
                          />
                        </Box>
                      ) : !odaOptions ? (
                        excludedReports(patientEpisodesData).length ? (
                          <Box textAlign="center" p={1}>
                            <ShareMenuIcon
                              onClick={() => {
                                const nonProviderReports = excludedReports(patientEpisodesData);
                                if (nonProviderReports.length > 0) {
                                  removeMatchingReportIds(nonProviderReports);
                                }
                                setIsODAPopupVisible(true);
                              }}
                              disabled={reportIdSelected.length === 0}
                            />
                          </Box>
                        ) : (
                          <Box textAlign="center" p={1}>
                            <ShareMenuIcon
                              onClick={() => {
                                setOpenShare(true);
                              }}
                              disabled={reportIdSelected.length === 0}
                            />
                          </Box>
                        )
                      ) : (
                        <Box textAlign="center" p={1}>
                          <ShareMenuIcon
                            onClick={() => {
                              setOpenShare(true);
                            }}
                            disabled={reportIdSelected.length === 0}
                          />
                        </Box>
                      )}
                      {/* {isSmscEnabled ? (
                        <Box textAlign="center" p={1}>
                          {isQueuedLoading ? (
                            <CircularProgress size={16} />
                          ) : (
                            <SendMenuIcon
                              onClick={onQueueDownloads(
                                reportIdSelected.map((r) => parseInt(r)),
                                queueDownloads,
                              )}
                              disabled={isQueuedLoading || reportIdSelected.length === 0}
                            />
                          )}
                        </Box>
                      ) : null} */}

                      <Box textAlign="center" p={1}>
                        {isDownloadLoading ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DownloadMenuIcon
                            onClick={downloadReports}
                            title={"Download Selected"}
                            disabled={isLoading || reportIdSelected.length === 0}
                          />
                        )}
                      </Box>

                      {!mobility ? (
                        <Box textAlign="center" p={1}>
                          {isLoading ? (
                            <CircularProgress size={16} />
                          ) : (
                            <PrintMenuIcon
                              onClick={printReports}
                              title={"Print Selected"}
                              disabled={isLoading || reportIdSelected.length === 0}
                            />
                          )}
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                </div>
              ) : (
                ""
              )}
              <hr style={classes.hr}></hr>
              <div style={classes.root}>
                <Box m={1}>
                  <Select
                    id="sort_by"
                    value={sortBy}
                    onChange={selectSort}
                    variant="outlined"
                    sx={isDesktop ? classes.selectInput : classes.selectInputMobile}
                  >
                    {/* <MenuItem value={0}>Sort By</MenuItem> */}
                    {getSortBy["sort_by"].map((item: any) => {
                      return (
                        <MenuItem sx={classes.menuItem} key={item.id} value={item.value}>
                          {item.value === sortBy ? "Sort By : " : ""}
                          {item.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Box>
              </div>
            </Box>
            <Box height="100vh" mx={1}>
              <Scrollbars>
                <PatientEpisodes
                  patientId={id}
                  setUrno={setUrno}
                  setWard={setWard}
                  setReferringDocotr={setReferringDoctor}
                  setLabnoDigitOnly={setLabnoDigitOnly}
                  labnoDigitOnly={labnoDigitOnly}
                  setInitialLabNo={setInitialLabNo}
                  allPatients={allPatients}
                  sortBy={sortData}
                  setChangeSelected={setReportIdSelected}
                  reportIdSelected={reportIdSelected}
                  setCheckEpisode={setUpdatedCheckEpisode}
                  allReportIds={allReportIds}
                  setAllReportIds={setAllReportIds}
                  allReportFetch={allReportFetch}
                  setAllReportFetch={setAllReportFetch}
                  showRemovedPanels={showRemovedPanels}
                  rescroll={rescroll}
                  setRescroll={setRescroll}
                  panel={panel}
                  setPanel={setPanel}
                  isDesktop={isDesktop}
                  print={print}
                  setPrint={setPrint}
                  patient={patientInfo}
                  onPatientEpisodesData={handlePatientEpisodesData}
                />
              </Scrollbars>
            </Box>
          </Fragment>
        )}
        <PopUpCard
          open={isODAPopupVisible}
          onClose={() => handleVisiblePopUp}
          onSetOpenOda={(st: boolean) => setIsODAPopupVisible(st)}
          previousReports={previousReports}
          removedReports={excludedReports(patientEpisodesData)}
          onContinue={handleOpenNextComponent}
        />
        {!allPatients ? (
          <DialogBoxShare
            open={openShare}
            onClose={() => setOpenShare(false)}
            onSetOpenShare={(st: boolean) => setOpenShare(st)}
            reports={Object.fromEntries(reportIdSelected.map((item) => [item, true]))}
            patient={id}
            labno={labnoDigitOnly}
          />
        ) : !odaOptions ? (
          showNextComponent && (
            <DialogBoxShare
              open={openShare}
              onClose={() => setOpenShare(false)}
              onSetOpenShare={(st: boolean) => setOpenShare(st)}
              reports={Object.fromEntries(reportIdSelected.map((item) => [item, true]))}
              patient={id}
              labno={labnoDigitOnly}
            />
          )
        ) : (
          <DialogBoxShare
            open={openShare}
            onClose={() => setOpenShare(false)}
            onSetOpenShare={(st: boolean) => setOpenShare(st)}
            reports={Object.fromEntries(reportIdSelected.map((item) => [item, true]))}
            patient={id}
            labno={labnoDigitOnly}
          />
        )}
        {patientError || printError ? <ErrorPopover /> : null}
        <SuccessPopover />
      </Box>

      <PrintFrame />
    </>
  );
};

export default PatientDetailsPage;
